import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { MEGA_WATT_CONVERSION_FACTOR } from "@/constants/common.constants";
import { getBaseUrl } from "@/helpers/fetch.helpers";
import { getJwtToken } from "@/helpers/storage.helpers";
import type {
  CreateEnergyBlockBody,
  EnergyBlock,
  EnergyBlockListParams,
  ResultantEnergyBlocks,
  ResultantEnergyBlocksData,
  ResultantEnergyBlocksParams,
  ResultantVolumesWh,
} from "@/pages/Energy";
import { energyScheduleExchangeApi } from "./energyScheduleExchangeApi";

export const contractedResultantsTransform = (response: {
  resultants: ResultantEnergyBlocksData[];
}): ResultantEnergyBlocks[] => {
  return response.resultants.map((item) => {
    const volumesMWh: ResultantVolumesWh = {};
    for (const [key, value] of Object.entries(item.volumesWh)) {
      volumesMWh[key as keyof ResultantVolumesWh] =
        (value ?? 0) / MEGA_WATT_CONVERSION_FACTOR;
    }
    return {
      ...item,
      volumesMWh,
      total: Object.values(volumesMWh).reduce(
        (acc, val) => acc + Number(val),
        0
      ),
    };
  });
};

export const energyExchangeApi = createApi({
  reducerPath: "energyExchangeApi",
  baseQuery: fetchBaseQuery({
    baseUrl: getBaseUrl("/energy-exchange/"),
    prepareHeaders: (headers) => {
      headers.set("authorization", `Bearer ${getJwtToken()}`);
      return headers;
    },
  }),
  tagTypes: [
    "EnergyBlock",
    "ResultantEnergyBlock",
    "ProfileCreatorDisabledResultant",
  ],
  endpoints: (build) => ({
    getEnergyBlocks: build.query<
      EnergyBlock[] | undefined,
      EnergyBlockListParams
    >({
      query: ({ deviceID, start, end, statuses = [], limit = 1000 }) => {
        return {
          url: "contracts",
          params: {
            deviceID,
            start,
            end,
            statuses: statuses.join(","),
            limit,
          },
        };
      },
      transformResponse: (response: { energyContracts: EnergyBlock[] }) =>
        response.energyContracts,
      providesTags: ["EnergyBlock"],
    }),
    createEnergyBlock: build.mutation<unknown, CreateEnergyBlockBody>({
      query: (body) => ({
        url: "energy-blocks",
        method: "POST",
        body,
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(
          energyScheduleExchangeApi.util.invalidateTags([
            "ScheduledResultantEnergyBlock",
          ])
        );
      },
      invalidatesTags: ["EnergyBlock", "ResultantEnergyBlock"],
    }),
    deleteEnergyBlock: build.mutation<unknown, string>({
      query: (id) => ({
        url: `contracts/${id}`,
        method: "DELETE",
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(
          energyScheduleExchangeApi.util.invalidateTags([
            "ScheduledResultantEnergyBlock",
          ])
        );
      },
      invalidatesTags: ["EnergyBlock", "ResultantEnergyBlock"],
    }),
    getResultantEnergyBlocks: build.query<
      ResultantEnergyBlocks[] | undefined,
      ResultantEnergyBlocksParams
    >({
      query: ({ deviceID, from, to }) => {
        return {
          url: "contracts/resultant",
          params: {
            deviceID: deviceID,
            start: from,
            end: to,
          },
        };
      },
      transformResponse: contractedResultantsTransform,
      providesTags: ["ResultantEnergyBlock"],
    }),
  }),
});

export const {
  useGetEnergyBlocksQuery,
  useGetResultantEnergyBlocksQuery,
  useDeleteEnergyBlockMutation,
  useCreateEnergyBlockMutation,
} = energyExchangeApi;
